import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlineKey, HiOutlineCheck, HiOutlineExclamation, HiOutlineRefresh, HiOutlineCube, HiOutlineLink, HiOutlinePhotograph } from 'react-icons/hi';

const AVAILABLE_MODELS = [
  { id: 'claude-opus-5', label: 'Claude Opus 5', tier: 'Most Capable' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5 (Default)', tier: 'Balanced' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', tier: 'Fastest' },
  { id: 'claude-fable-5-1', label: 'Claude Fable 5.1', tier: 'Creative' },
];

const ALL_ASSET_TYPES = {
  // Partner
  blog: { label: 'Blog Post', dashboard: 'Partner' },
  newsletter: { label: 'Newsletter', dashboard: 'Partner' },
  email_sequence: { label: 'Email Sequence', dashboard: 'Partner' },
  one_pager: { label: 'One-Pager', dashboard: 'Partner' },
  banner: { label: 'Banner', dashboard: 'Partner' },
  service_page: { label: 'Service Page', dashboard: 'Partner' },
  presentation: { label: 'Presentation', dashboard: 'Partner' },
  marketing_strategy: { label: 'Marketing Strategy', dashboard: 'Partner' },
  // CS
  cs_asset_1: { label: 'CS Asset 1', dashboard: 'CS' },
  cs_asset_2: { label: 'CS Asset 2', dashboard: 'CS' },
  cs_asset_3: { label: 'CS Asset 3', dashboard: 'CS' },
  cs_asset_4: { label: 'CS Asset 4', dashboard: 'CS' },
  // Sales
  sales_proposal: { label: 'Proposal', dashboard: 'Sales' },
  sales_deck: { label: 'Sales Deck', dashboard: 'Sales' },
  customer_overview: { label: 'Customer Overview', dashboard: 'Sales' },
  customer_status: { label: 'Customer Status Report', dashboard: 'Sales' },
  customer_one_pager: { label: 'Customer One-Pager', dashboard: 'Sales' },
};

const DEFAULT_MODEL = 'claude-sonnet-5';

const DASHBOARD_GROUPS = ['Partner', 'CS', 'Sales'];

export default function AdminSettings() {
  const [apiKey, setApiKey] = useState('');
  const [maskedKey, setMaskedKey] = useState(null);
  const [isSet, setIsSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // Model settings
  const [modelAssignments, setModelAssignments] = useState({});
  const [savingModels, setSavingModels] = useState(false);
  const [modelsChanged, setModelsChanged] = useState(false);

  // HubSpot settings
  const [hsKey, setHsKey] = useState('');
  const [hsMaskedKey, setHsMaskedKey] = useState(null);
  const [hsIsSet, setHsIsSet] = useState(false);
  const [hsSaving, setHsSaving] = useState(false);
  const [hsTesting, setHsTesting] = useState(false);
  const [hsTestResult, setHsTestResult] = useState(null);

  // Branding settings
  const [branding, setBranding] = useState({ logoUrl: '', companyName: 'V2 Cloud', primaryColor: '#0066CC', accentColor: '#00B4D8', website: 'https://v2cloud.com', defaultCalendlyUrl: '' });
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingChanged, setBrandingChanged] = useState(false);
  const [uploadingBrandingLogo, setUploadingBrandingLogo] = useState(false);
  const brandingLogoRef = useRef();

  const handleBrandingLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBrandingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const { data } = await api.post('/settings/branding-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBranding(data.branding);
      setBrandingChanged(false);
      addToast('V2 Cloud logo uploaded');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to upload logo', 'error');
    } finally {
      setUploadingBrandingLogo(false);
      if (brandingLogoRef.current) brandingLogoRef.current.value = '';
    }
  };

  const { addToast } = useToast();

  useEffect(() => {
    Promise.all([
      api.get('/settings/api-key'),
      api.get('/settings/models'),
      api.get('/settings/hubspot-key'),
      api.get('/settings/branding')
    ]).then(([keyRes, modelsRes, hsRes, brandingRes]) => {
      setIsSet(keyRes.data.isSet);
      setMaskedKey(keyRes.data.maskedKey);
      setModelAssignments(modelsRes.data);
      setHsIsSet(hsRes.data.isSet);
      setHsMaskedKey(hsRes.data.maskedKey);
      setBranding(brandingRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!apiKey.trim()) { addToast('Please enter an API key', 'error'); return; }
    if (!apiKey.startsWith('sk-')) { addToast('API key must start with sk-', 'error'); return; }
    setSaving(true);
    try {
      const { data } = await api.put('/settings/api-key', { apiKey });
      setMaskedKey(data.maskedKey);
      setIsSet(true);
      setApiKey('');
      setTestResult(null);
      addToast('API key saved successfully');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to save API key', 'error');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = apiKey.trim() ? { apiKey } : {};
      const { data } = await api.post('/settings/test-api', payload);
      setTestResult({ success: true, response: data.response, model: data.model });
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.error || 'Test failed' });
    }
    setTesting(false);
  };

  const handleModelChange = (assetType, modelId) => {
    setModelAssignments(prev => ({ ...prev, [assetType]: modelId }));
    setModelsChanged(true);
  };

  const handleSaveModels = async () => {
    setSavingModels(true);
    try {
      await api.put('/settings/models', { models: modelAssignments });
      addToast('Model assignments saved');
      setModelsChanged(false);
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to save', 'error');
    }
    setSavingModels(false);
  };

  const handleSetAllModels = (modelId) => {
    const updated = {};
    Object.keys(ALL_ASSET_TYPES).forEach(type => { updated[type] = modelId; });
    setModelAssignments(updated);
    setModelsChanged(true);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Settings</h1>
      <p className="text-gray-500 mb-8">Manage your portal configuration.</p>

      {/* API Key Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <HiOutlineKey className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Claude API Key</h2>
            <p className="text-sm text-gray-500">Required for asset generation</p>
          </div>
        </div>

        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-4 ${isSet ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
          {isSet ? (
            <><HiOutlineCheck className="w-4 h-4" /><span className="text-sm font-medium">API key configured: {maskedKey}</span></>
          ) : (
            <><HiOutlineExclamation className="w-4 h-4" /><span className="text-sm font-medium">No API key set. Asset generation will not work.</span></>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{isSet ? 'Replace API Key' : 'Enter API Key'}</label>
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-ant-api03-..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
          <p className="text-xs text-gray-400 mt-1">
            Get your API key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-v2-blue hover:underline">console.anthropic.com</a>
          </p>
        </div>

        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving || !apiKey.trim()}
            className="px-4 py-2 bg-v2-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {saving ? <><HiOutlineRefresh className="w-4 h-4 animate-spin" />Saving...</> : 'Save Key'}
          </button>
          <button onClick={handleTest} disabled={testing || (!isSet && !apiKey.trim())}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {testing ? <><HiOutlineRefresh className="w-4 h-4 animate-spin" />Testing...</> : 'Test API Connection'}
          </button>
        </div>

        {testResult && (
          <div className={`mt-4 p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {testResult.success ? (
              <div>
                <div className="flex items-center gap-2 text-green-700 font-medium mb-1"><HiOutlineCheck className="w-4 h-4" />API Connection Successful</div>
                <p className="text-sm text-green-600">Model: {testResult.model}</p>
                <p className="text-sm text-green-600">Response: {testResult.response}</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 text-red-700 font-medium mb-1"><HiOutlineExclamation className="w-4 h-4" />API Connection Failed</div>
                <p className="text-sm text-red-600">{testResult.error}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* HubSpot API Key Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <HiOutlineLink className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">HubSpot API Key</h2>
            <p className="text-sm text-gray-500">Required for CRM integration (PM dashboard, Sales import)</p>
          </div>
        </div>

        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-4 ${hsIsSet ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
          {hsIsSet ? (
            <><HiOutlineCheck className="w-4 h-4" /><span className="text-sm font-medium">HubSpot key configured: {hsMaskedKey}</span></>
          ) : (
            <span className="text-sm">No HubSpot key set. CRM features will be unavailable.</span>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{hsIsSet ? 'Replace HubSpot Key' : 'Enter HubSpot Private App Token'}</label>
          <input type="password" value={hsKey} onChange={(e) => setHsKey(e.target.value)} placeholder="pat-na1-..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
          <p className="text-xs text-gray-400 mt-1">Create a private app in HubSpot Settings &gt; Integrations &gt; Private Apps</p>
        </div>

        <div className="flex gap-3">
          <button onClick={async () => {
            if (!hsKey.trim()) { addToast('Please enter a HubSpot API key', 'error'); return; }
            setHsSaving(true);
            try {
              const { data } = await api.put('/settings/hubspot-key', { apiKey: hsKey });
              setHsMaskedKey(data.maskedKey);
              setHsIsSet(true);
              setHsKey('');
              setHsTestResult(null);
              addToast('HubSpot API key saved');
            } catch (err) {
              addToast(err.response?.data?.error || 'Failed to save', 'error');
            }
            setHsSaving(false);
          }} disabled={hsSaving || !hsKey.trim()}
            className="px-4 py-2 bg-v2-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {hsSaving ? <><HiOutlineRefresh className="w-4 h-4 animate-spin" />Saving...</> : 'Save Key'}
          </button>
          <button onClick={async () => {
            setHsTesting(true);
            setHsTestResult(null);
            try {
              const { data } = await api.post('/settings/test-hubspot');
              setHsTestResult({ success: true, portalId: data.portalId });
            } catch (err) {
              setHsTestResult({ success: false, error: err.response?.data?.error || 'Test failed' });
            }
            setHsTesting(false);
          }} disabled={hsTesting || !hsIsSet}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {hsTesting ? <><HiOutlineRefresh className="w-4 h-4 animate-spin" />Testing...</> : 'Test Connection'}
          </button>
        </div>

        {hsTestResult && (
          <div className={`mt-4 p-4 rounded-lg ${hsTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {hsTestResult.success ? (
              <div className="flex items-center gap-2 text-green-700 font-medium"><HiOutlineCheck className="w-4 h-4" />Connected to HubSpot (Portal: {hsTestResult.portalId})</div>
            ) : (
              <div className="flex items-center gap-2 text-red-700 font-medium"><HiOutlineExclamation className="w-4 h-4" />{hsTestResult.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Branding Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <HiOutlinePhotograph className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">V2 Cloud Branding</h2>
            <p className="text-sm text-gray-500">Logo and brand colors used in generated assets</p>
          </div>
        </div>

        {branding.logoUrl && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
            <img src={branding.logoUrl} alt="Logo preview" className="h-8 max-w-[200px] object-contain" onError={e => { e.target.style.display = 'none'; }} />
            <span className="text-xs text-gray-400">Current logo preview</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => brandingLogoRef.current?.click()} disabled={uploadingBrandingLogo}
                className="inline-flex items-center gap-2 px-4 py-2 bg-v2-blue text-white rounded-lg text-sm font-medium disabled:opacity-50">
                <HiOutlinePhotograph className="w-4 h-4" /> {uploadingBrandingLogo ? 'Uploading…' : 'Upload logo'}
              </button>
              <span className="text-xs text-gray-400">PNG, SVG, JPG or WEBP (max 2MB). Applies immediately.</span>
            </div>
            <input ref={brandingLogoRef} type="file" accept=".png,.svg,.jpg,.jpeg,.webp" onChange={handleBrandingLogoUpload} className="hidden" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">…or Logo URL</label>
            <input type="url" value={branding.logoUrl} onChange={e => { setBranding(p => ({ ...p, logoUrl: e.target.value })); setBrandingChanged(true); }}
              placeholder="https://v2cloud.com/wp-content/uploads/2024/01/v2-cloud-logo.png"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
            <p className="text-xs text-gray-400 mt-1">Uploading sets this automatically. Or paste a direct link to your logo image.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input type="text" value={branding.companyName} onChange={e => { setBranding(p => ({ ...p, companyName: e.target.value })); setBrandingChanged(true); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input type="url" value={branding.website} onChange={e => { setBranding(p => ({ ...p, website: e.target.value })); setBrandingChanged(true); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Calendly URL <span className="text-xs text-gray-400 font-normal">(fallback when no Partner Manager assigned)</span></label>
            <input type="url" value={branding.defaultCalendlyUrl || ''} onChange={e => { setBranding(p => ({ ...p, defaultCalendlyUrl: e.target.value })); setBrandingChanged(true); }}
              placeholder="https://calendly.com/v2cloud-partnerships/intro-call"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
            <p className="text-xs text-gray-400 mt-1">Used in Partner dashboard "Schedule a Call" when no PM is assigned or PM has no Calendly URL</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Brand Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={branding.primaryColor} onChange={e => { setBranding(p => ({ ...p, primaryColor: e.target.value })); setBrandingChanged(true); }}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                <input type="text" value={branding.primaryColor} onChange={e => { setBranding(p => ({ ...p, primaryColor: e.target.value })); setBrandingChanged(true); }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent font-mono text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={branding.accentColor} onChange={e => { setBranding(p => ({ ...p, accentColor: e.target.value })); setBrandingChanged(true); }}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                <input type="text" value={branding.accentColor} onChange={e => { setBranding(p => ({ ...p, accentColor: e.target.value })); setBrandingChanged(true); }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent font-mono text-sm" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <button onClick={async () => {
            setSavingBranding(true);
            try {
              await api.put('/settings/branding', branding);
              addToast('Branding saved successfully');
              setBrandingChanged(false);
            } catch (err) { addToast(err.response?.data?.error || 'Failed to save branding', 'error'); }
            setSavingBranding(false);
          }} disabled={savingBranding || !brandingChanged}
            className="px-4 py-2 bg-v2-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {savingBranding ? <><HiOutlineRefresh className="w-4 h-4 animate-spin" />Saving...</> : 'Save Branding'}
          </button>
        </div>
      </div>

      {/* Model Assignment Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <HiOutlineCube className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">LLM Model per Asset Type</h2>
            <p className="text-sm text-gray-500">Choose which Claude model generates each asset type</p>
          </div>
        </div>

        {/* Quick set all */}
        <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">Set all to:</span>
          {AVAILABLE_MODELS.slice(0, 3).map(m => (
            <button key={m.id + m.tier} onClick={() => handleSetAllModels(m.id)}
              className="px-3 py-1 text-xs font-medium rounded-full border border-gray-300 hover:bg-v2-blue hover:text-white hover:border-v2-blue transition-colors">
              {m.label.replace(' (Default)', '')}
            </button>
          ))}
        </div>

        {/* Per-dashboard groups */}
        {DASHBOARD_GROUPS.map(dashboard => {
          const types = Object.entries(ALL_ASSET_TYPES).filter(([, v]) => v.dashboard === dashboard);
          return (
            <div key={dashboard} className="mb-6 last:mb-0">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${dashboard === 'Partner' ? 'bg-blue-500' : dashboard === 'CS' ? 'bg-teal-500' : 'bg-orange-500'}`} />
                {dashboard} Assets
              </h3>
              <div className="space-y-2">
                {types.map(([type, config]) => (
                  <div key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">{config.label}</span>
                      <span className="text-xs text-gray-400 ml-2">{type}</span>
                    </div>
                    <select
                      value={modelAssignments[type] || DEFAULT_MODEL}
                      onChange={e => handleModelChange(type, e.target.value)}
                      className="w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent bg-white"
                    >
                      {AVAILABLE_MODELS.slice(0, 3).map(m => (
                        <option key={m.id + m.tier} value={m.id}>{m.label.replace(' (Default)', '')} ({m.tier})</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Save button */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button onClick={handleSaveModels} disabled={savingModels || !modelsChanged}
            className="px-4 py-2 bg-v2-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {savingModels ? <><HiOutlineRefresh className="w-4 h-4 animate-spin" />Saving...</> : 'Save Model Assignments'}
          </button>
          {!modelsChanged && (
            <p className="text-xs text-gray-400 mt-2">No changes to save. Unassigned types use Claude Sonnet 4 by default.</p>
          )}
        </div>
      </div>
    </div>
  );
}
